import React, { useState, useEffect } from 'react';
import { Search, Loader, Info } from 'lucide-react';

const SOURCES = [
  { key: 'pubmed', label: 'PubMed (NCBI E-utilities)' },
  { key: 'europepmc', label: 'Europe PMC' },
  { key: 'clinicaltrials', label: 'ClinicalTrials.gov' },
  { key: 'semanticscholar', label: 'Semantic Scholar' },
];

export default function QueryPlanPreview({ plan, onPlanChange, onSearch, loading }) {
  const [local, setLocal] = useState(plan);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => { setLocal(plan); setIsDirty(false); }, [plan]);

  function patch(path, value) {
    setLocal(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return next;
    });
    setIsDirty(true);
    onPlanChange && onPlanChange(local); // parent tracks dirty state
  }

  function handleSearch() {
    onSearch(local, isDirty ? 'manual_edit' : 'ai_generated');
  }

  const structured = local.structured || {};

  return (
    <div className="fs-plan-preview">
      <div className="fs-plan-rationale">
        <Info size={14} />
        <span>{local.rationale}</span>
        <span className="fs-plan-confidence">Confidence: {Math.round((local.confidence || 0) * 100)}%</span>
      </div>

      <div className="fs-plan-section-title">Structured Filters</div>
      <div className="fs-plan-grid">
        {[
          ['search_terms', 'Search Terms'],
          ['mesh_terms', 'MeSH Terms'],
          ['condition_filter', 'Condition'],
          ['study_type_filter', 'Study Type'],
        ].map(([key, label]) => (
          <div key={key} className="fs-plan-field">
            <label className="fs-plan-field-label">{label}</label>
            <input
              className="fs-plan-input"
              value={structured[key] || ''}
              onChange={e => patch(`structured.${key}`, e.target.value)}
            />
          </div>
        ))}
        <div className="fs-plan-field">
          <label className="fs-plan-field-label">Date Range (months)</label>
          <input
            className="fs-plan-input fs-plan-input--sm"
            type="number" min={1} max={240}
            value={structured.date_range_months || 12}
            onChange={e => patch('structured.date_range_months', parseInt(e.target.value) || 12)}
          />
        </div>
      </div>

      <div className="fs-plan-section-title">Per-Source Native Queries</div>
      {SOURCES.map(({ key, label }) => (
        <div key={key} className="fs-plan-field">
          <label className="fs-plan-field-label">{label}</label>
          <textarea
            className="fs-plan-native"
            rows={3}
            value={local.sources?.[key]?.native_query || ''}
            onChange={e => patch(`sources.${key}.native_query`, e.target.value)}
          />
        </div>
      ))}

      {isDirty && (
        <div className="fs-plan-edit-notice">
          You edited the query — it will execute as "manual edit".
        </div>
      )}

      <div className="fs-plan-actions">
        <button className="fs-btn fs-btn--primary" onClick={handleSearch} disabled={loading}>
          {loading ? <Loader size={14} className="fs-spin" /> : <Search size={14} />}
          {loading ? 'Searching…' : 'Run Search'}
        </button>
      </div>
    </div>
  );
}
